import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { KeycloakService } from 'keycloak-angular';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { titles } from '../configuration/job-information-fields.config';
import { USER_DATA_CONFIG, UserDataConfigInterface } from '../configuration/user-data-config.interface';
import { ApiAreaOfInterest, ApiEmployeeCount, AreaOfInterest, EmployeeCount } from '../interfaces/commons.interface';
import { JobInformation } from '../interfaces/job-information.interface';

@Injectable()
export class UserDataService {
  private getUserId(): string | null {
    return this.keycloakService.getKeycloakInstance()?.tokenParsed?.sub ?? null;
  }

  enrichJobInformation<T extends JobInformation>(
    t: T,
    countries: { id: string; name: string }[],
    employeeCountOptions: EmployeeCount[] | undefined,
    areaOfInterestOptions: AreaOfInterest[] | undefined
  ): Observable<T> {
    return of({
      ...t,
      title: titles.find((title) => title.key === t.title)?.label,
      country_name: countries.find((country) => country.id === t.country_name)?.name,
      employee_count:
        employeeCountOptions
          ?.filter((option) => option.id === t.employee_count)
          .map((option) => option.text + $localize`:@@featureBusinessCard-employee: Mitarbeiter`)[0] ?? undefined,
      area_of_interest:
        areaOfInterestOptions?.filter((option) => option.id === t.area_of_interest).map((option) => option.text)[0] ??
        undefined,
    });
  }

  getEmployeeCountOptions(): Observable<EmployeeCount[]> {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }
    return this.httpClient
      .get<ApiEmployeeCount>(`${this.vCardConfig.userServiceApiURL}/translations/EMPLOYEE_COUNT`)
      .pipe(
        map((response) =>
          response.translations.map((item) => {
            return {
              id: item.key,
              text: item.text,
            };
          })
        )
      );
  }

  getAreaOfInterestOptions(): Observable<AreaOfInterest[]> {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }
    return this.httpClient
      .get<ApiAreaOfInterest>(`${this.vCardConfig.userServiceApiURL}/translations/AREA_OF_INTEREST`)
      .pipe(
        map((response) =>
          response.translations.map((item) => {
            return {
              id: item.key,
              text: item.text,
            };
          })
        )
      );
  }

  constructor(
    private httpClient: HttpClient,
    @Inject(USER_DATA_CONFIG) private vCardConfig: UserDataConfigInterface,
    private keycloakService: KeycloakService
  ) {}
}
